import React from 'react';
import { Box, BoxProps, Flex, JoyKitThemeProvider, TextV2, TooltipV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { Help } from '@withjoy/joykit/icons';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { SoldOutTag } from '@shared/components/SoldOutTag/SoldOutTag';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useFormattedDate } from '@shared/hooks/useFormattedDate';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { IsoCurrencyIndicator } from '@shared/components/IsoCurrencyIndicator';
import { PercentageOffBadge } from '@shared/components/PercentageOffBadge';
import type { HotelTilePricingProps } from '../HotelTileCard.types';
import { useEventInfo } from '@shared/utils/eventInfo';
import { HotelTilePricingSkeleton } from './HotelTilePricing.skeleton';
import { pxToRem } from '@withjoy/joykit/theme';

export const HotelTilePricing: React.FC<HotelTilePricingProps & BoxProps> = ({ data, accommodationId, roomBlock, ...boxProps }) => {
  const { tq } = useTranslation('hotelTile');
  const { eventInfo } = useEventInfo();
  const eventDisplayName = eventInfo?.eventDisplayName;

  const shouldFetchPricing = !data && !!accommodationId;

  // Mode 2: Fetch pricing internally if accommodationId is provided
  const {
    price: fetchedPrice,
    strikeOutPrice: fetchedStrikeoutPrice,
    percentageOff: fetchedPercentageOff,
    loading,
    soldOutUrl,
  } = useTravelPlacesPricingAndBookingDetailsFragment(
    accommodationId || '',
    'accommodationPlace',
    !shouldFetchPricing, // skip if no accommodationId
  );

  // Determine which pricing mode to use
  const price = data?.price || fetchedPrice;
  const strikeoutPrice = data?.strikeoutPrice || fetchedStrikeoutPrice;
  const percentageOff = data?.percentageOff || fetchedPercentageOff;
  const isSoldOut = data?.soldOut || !!soldOutUrl;
  const showEventRateTag = data?.eventOnlyRate !== false;

  // Mode 3: Room block pricing
  if (roomBlock) {
    return (
      <RoomBlockPricing
        roomBlock={roomBlock}
        {...boxProps}
      />
    );
  }

  // Handle sold out state
  if (isSoldOut) {
    return (
      <Box
        {...boxProps}
        marginTop={5}
      >
        <SoldOutTag />
      </Box>
    );
  }

  // If no pricing data, don't render
  if (!price && !loading) {
    return null;
  }

  return (
    <JoyKitThemeProvider>
      <Box {...boxProps}>
        <SkeletonGroup
          isReady={!loading}
          placeholder={<HotelTilePricingSkeleton />}
        >
          {price && (
            <Flex
              flexDirection="column"
              gap={3}
              borderTop="1px solid"
              borderTopColor="mono3"
              paddingTop={4}
            >
              {/* Top Row: Special Event Rate (left) and Save Badge (right) */}
              <Flex
                alignItems="center"
                justifyContent="space-between"
              >
                {showEventRateTag && (
                  <Flex
                    alignItems="center"
                    gap={1}
                  >
                    <TextV2
                      typographyVariant="button1"
                      color="mono14"
                      fontWeight={600}
                      letterSpacing="-0.27px"
                    >
                      {tq`Special Event Rate`}
                    </TextV2>
                    <TooltipV2
                      content={tq`${eventDisplayName}'s exclusive guest rates are available when you book with Joy. Reserve soon here to lock in this price—rooms are limited.`}
                      onMouseEnterDelay={500}
                      placement="topEnd"
                      overrides={{
                        Content: {
                          props: {
                            backgroundColor: 'white',
                            color: 'mono14',
                            maxWidth: pxToRem(256),
                            typographyVariant: 'label2',
                          },
                        },
                        Arrow: { props: { backgroundColor: 'white' } },
                      }}
                    >
                      <Help
                        size="sm"
                        color="mono14"
                        title={undefined}
                      />
                    </TooltipV2>
                  </Flex>
                )}
                {percentageOff && <PercentageOffBadge percentage={percentageOff} />}
              </Flex>

              {/* Bottom Row: "Nightly price from" (left) and Prices (right) */}
              <Flex
                alignItems="baseline"
                justifyContent="space-between"
              >
                <TextV2
                  typographyVariant="body1"
                  color="mono8"
                  fontWeight={400}
                >
                  {tq`Nightly price from`}
                </TextV2>
                <Flex
                  gap={1}
                  alignItems="baseline"
                >
                  {/* Strikeout Price */}
                  {strikeoutPrice && (
                    <TextV2
                      typographyVariant="hed2"
                      textDecoration="line-through"
                      color="mono8"
                      fontWeight={400}
                    >
                      <IsoCurrencyIndicator />
                      {strikeoutPrice}
                    </TextV2>
                  )}
                  {/* Main Price */}
                  <TextV2
                    typographyVariant="hed6"
                    color="mono14"
                    fontWeight={600}
                    letterSpacing="-0.27px"
                  >
                    <IsoCurrencyIndicator />
                    {price}
                  </TextV2>
                </Flex>
              </Flex>
            </Flex>
          )}
        </SkeletonGroup>
      </Box>
    </JoyKitThemeProvider>
  );
};

// Room block pricing sub-component
const RoomBlockPricing: React.FC<{ roomBlock: NonNullable<HotelTilePricingProps['roomBlock']> } & BoxProps> = ({ roomBlock, ...boxProps }) => {
  const { tq } = useTranslation('hotelTile');
  const { pricePerNight, strikeoutPricePerNight, cutoffDate } = roomBlock;

  const cutoffDateObj = cutoffDate ? createDateFromUnformattedString(cutoffDate) : null;
  const formattedCutoffDate = useFormattedDate(cutoffDateObj, 'MMM dd, yyyy');

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      paddingTop={5}
      {...boxProps}
    >
      {cutoffDate && (
        <Flex
          flexDirection="column"
          gap="1"
        >
          <TextV2
            typographyVariant="body1"
            color="accentGreen3"
            fontFamily="Inter UI"
            fontWeight={400}
            letterSpacing="-0.15px"
          >
            {tq`Book before`}
          </TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono10"
            fontFamily="Inter UI"
            fontWeight={400}
            letterSpacing="-0.15px"
          >
            {formattedCutoffDate}
          </TextV2>
        </Flex>
      )}
      <Flex
        flexDirection="column"
        gap="1"
      >
        <Flex
          alignItems="center"
          gap={1}
        >
          <TextV2
            typographyVariant="body1"
            fontFamily="Inter UI"
            color="accentGreen3"
            fontWeight={400}
            textAlign="right"
          >
            {tq`Group Rate`}
          </TextV2>
          <TooltipV2
            content={tq`A group rate is a special discounted price arranged for wedding guests at this hotel.`}
            onMouseEnterDelay={500}
            placement="topEnd"
            overrides={{
              Content: { props: { backgroundColor: 'white', color: 'mono14', maxWidth: '256px', typographyVariant: 'label2' } },
              Arrow: { props: { backgroundColor: 'white' } },
            }}
          >
            <Help
              size="sm"
              color="accentGreen3"
              title={undefined}
            />
          </TooltipV2>
        </Flex>
        <Flex
          gap={2}
          justifyContent="flex-end"
        >
          {!!strikeoutPricePerNight && (
            <TextV2
              typographyVariant="body3"
              color="mono10"
              fontFamily="Inter UI"
              fontWeight={400}
              textAlign="right"
              backgroundSize="100% 1.5px"
              width="auto"
              backgroundImage="linear-gradient(currentColor, currentColor)"
              backgroundPosition="0% 50%"
              backgroundRepeat="no-repeat"
            >
              {tq`$${strikeoutPricePerNight}`}
            </TextV2>
          )}
          <TextV2
            typographyVariant="hed2"
            color="mono14"
            fontFamily="Inter UI"
            fontWeight={600}
            textAlign="right"
            letterSpacing="-0.272px"
            textTransform="none"
          >
            {tq`$${pricePerNight}/night`}
          </TextV2>
        </Flex>
      </Flex>
    </Flex>
  );
};
